import { TFunction } from 'i18next';

import { IPaymentResult, IPaymentResultDto, PaymentStatus, PaymentType } from './commonTypes';

export async function getPayment(resourceToken: string): Promise<IGetPaymentResult> {
  const headers = new Headers();
  headers.set('Content-Type', 'application/json');
  headers.set('Authorization', `bearer ${resourceToken}`);

  const requestUrl = `${process.env.REACT_APP_BFF_URL}/payment`;
  const requestOptions: RequestInit = {
    headers: headers,
    method: 'GET',
  };
  const response = await fetch(requestUrl, requestOptions);

  if (!response.ok) {
    const serializedBody = await response.json();
    throw new Error(serializedBody);
  } else {
    const getPaymentResultDto = (await response.json()) as IGetPaymentResultDto;
    const getPaymentResult = mapFromDto(getPaymentResultDto);

    return getPaymentResult;
  }
}

function mapFromDto(resultDto: IGetPaymentResultDto): IGetPaymentResult {
  return {
    amountMinor: resultDto.amount_minor,
    branding: {
      colors: resultDto.branding.colors,
      logoUrl: resultDto.branding.logo_url,
      name: resultDto.branding.name,
    },
    currency: resultDto.currency,
    customer: {
      companyName: resultDto.customer?.company_name,
      email: resultDto.customer?.email,
      firstName: resultDto.customer?.first_name,
      lastName: resultDto.customer?.last_name,
      postalAddress: {
        line1: resultDto.customer?.postal_address?.line1,
        line2: resultDto.customer?.postal_address?.line2,
        number: resultDto.customer?.postal_address?.number,
        state: resultDto.customer?.postal_address?.state,
        townCity: resultDto.customer?.postal_address?.town_city,
        zip: resultDto.customer?.postal_address?.zip,
      },
    },
    description: resultDto.description,
    expiresAt: resultDto.expires_at,
    id: resultDto.id,
    links: resultDto._links,
    paymentMethodSelected: resultDto.payment_method_selected,
    paymentMethods: resultDto.payment_methods.map<IPaymentMethod>((item) => ({
      iconUrl: item.icon_url,
      morLegalName: item.mor_legal_name,
      name: item.name,
      type: item.type,
    })),
    requiresStoreCredentials: resultDto.requires_store_credentials,
    status: resultDto.status,
    tokenization: {
      merchantId: resultDto.tokenization.merchant_id,
      staticKey: resultDto.tokenization.static_key,
    },
    workflow: resultDto.workflow,
  };
}

interface IGetPaymentResultDto extends IPaymentResultDto {
  id: string;
  amount_minor: number;
  currency: string;
  expires_at?: string;
  description?: string;
  branding: IBrandingDto;
  payment_methods: IPaymentMethodDto[];
  payment_method_selected?: PaymentType;
  tokenization: ITokenizationDto;
  requires_store_credentials: boolean;
  customer?: ICustomerDto;
}

interface IBrandingDto {
  name: string;
  logo_url: string;
  colors: IColors;
}

interface IPaymentMethodDto {
  type: PaymentType;
  name: string;
  icon_url: string;
  mor_legal_name: string;
}

interface ITokenizationDto {
  merchant_id: string;
  static_key: string;
}

interface IPostalAddressDto {
  line1?: string;
  line2?: string;
  number?: string;
  town_city?: string;
  state?: string;
  zip?: string;
}

interface ICustomerDto {
  first_name?: string;
  last_name?: string;
  email?: string;
  postal_address?: IPostalAddressDto;
  company_name?: string;
}

interface IGetPaymentResult extends IPaymentResult {
  id: string;
  amountMinor: number;
  currency: string;
  expiresAt?: string;
  description?: string;
  branding: IBranding;
  paymentMethods: IPaymentMethod[];
  paymentMethodSelected?: PaymentType;
  status: PaymentStatus;
  tokenization: ITokenization;
  requiresStoreCredentials: boolean;
  customer?: ICustomer;
}

export interface IBranding {
  name: string;
  logoUrl: string;
  colors: IColors;
}

export interface IPaymentMethod {
  type: PaymentType;
  name: string;
  iconUrl: string;
  morLegalName: string;
}

interface IColors {
  primary: string;
  secondary: string;
}

interface ITokenization {
  merchantId: string;
  staticKey: string;
}

interface IPostalAddress {
  line1?: string;
  line2?: string;
  number?: string;
  townCity?: string;
  state?: string;
  zip?: string;
}

export interface ICustomer {
  firstName?: string;
  lastName?: string;
  email?: string;
  postalAddress?: IPostalAddress;
  companyName?: string;
}
